
import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { getAuthHeader } from "@/lib/authHelpers";
import { AlertTriangle, Loader2, Store } from "lucide-react";
import { useAuth } from "@/lib/authContext";
import { LoginModal } from "./LoginModal";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/lib/i18n";

interface ReportShopDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    shopId: string;
    shopName: string;
}

export function ReportShopDialog({ open, onOpenChange, shopId, shopName }: ReportShopDialogProps) {
    const { t } = useI18n();
    const { isLoggedIn } = useAuth();
    const { toast } = useToast();
    const [reason, setReason] = useState<string>("");
    const [description, setDescription] = useState(""); // Not used by backend yet for shops, but good for future
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const REPORT_REASONS = [
        { id: "NOT_A_KRINGLOOP", label: t("reportShop.reason.notThrift"), color: "red" },
        { id: "CLOSED_PERMANENTLY", label: t("reportShop.reason.closedPermanently"), color: "red" },
        { id: "CLOSED", label: t("reportShop.reason.closedTemporary"), color: "orange" },
        { id: "WRONG_INFO", label: t("reportShop.reason.wrongInfo"), color: "orange" },
        { id: "OTHER", label: t("reportShop.reason.other"), color: "gray" }
    ];

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!reason) return;

        if (!isLoggedIn) {
            setShowLoginModal(true);
            return;
        }

        try {
            setIsSubmitting(true);
            const authHeader = await getAuthHeader();

            const response = await fetch(`/api/shops/${shopId}/report`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(authHeader || {})
                },
                body: JSON.stringify({
                    reason,
                    description: description || undefined // Backend might ignore this currently but good to send
                })
            });

            if (response.ok) {
                const data = await response.json();

                let title = t("report.thanksTitle");
                let desc = t("reportShop.checking");

                if (reason === "CLOSED_PERMANENTLY") {
                    // If backend processed it automatically
                    desc = t("reportShop.closedPermanentlyDesc");
                }

                toast({
                    title: title,
                    description: desc,
                });

                onOpenChange(false);
                setReason("");
                setDescription("");
            } else {
                const error = await response.json();
                throw new Error(error.error || t("report.submitFailed"));
            }
        } catch (error) {
            console.error("Error submitting shop report:", error);
            toast({
                title: t("report.errorTitle"),
                description: t("report.errorDesc"),
                variant: "destructive"
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-yellow-500" />
                            {t("reportShop.titleAbout", { shopName })}
                        </DialogTitle>
                        <DialogDescription>
                            {t("reportShop.desc")}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-4 py-4">
                        <RadioGroup value={reason} onValueChange={setReason} className="space-y-3">
                            {REPORT_REASONS.map((r) => (
                                <div key={r.id} className="flex items-start space-x-2">
                                    <RadioGroupItem value={r.id} id={r.id} className="mt-1" />
                                    <Label htmlFor={r.id} className="cursor-pointer font-normal leading-tight">
                                        {r.label}
                                    </Label>
                                </div>
                            ))}
                        </RadioGroup>

                        {reason === "OTHER" && (
                            <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                                <Label htmlFor="description">{t("reportShop.explanation")}</Label>
                                <Textarea
                                    id="description"
                                    placeholder={t("reportShop.tellMore")}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="resize-none"
                                    rows={3}
                                />
                            </div>
                        )}

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
                                {t("shops.cancel")}
                            </Button>
                            <Button type="submit" disabled={!reason || isSubmitting} variant="destructive">
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t("report.sending")}
                                    </>
                                ) : (
                                    t("report.submit")
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
            <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
        </>
    );
}
